// Employee master — employee-master.html: people on the left, the record on
// the right as eight tabs (profile, employment, documents, salary, sales role,
// leave, assets, activity), a "Needs action" rail for expiring papers, and a
// document-expiry drawer.
//
// Built on the administration kit (AdminScreen). The API is new — every
// endpoint and field is specified in docs/BACKEND-COMPLETE.md (Part 4.3).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { setheadertoggle } from "../../redux/toggle";
import { fetchBranchList } from "../../redux/branchlist";
import {
  bulkUpdateEmployees,
  deleteEmployee,
  getEmployeeList,
  getEmployeeLookups,
  getNextEmployeeCode,
  sendRenewalReminder,
} from "../../services/EmployeeApiServices";
import {
  exportMasterExcel,
  exportMasterPDF,
  printMasterReport,
} from "../../core/utils/MasterReportUtils";
import {
  AdminScreen,
  AdminList,
  AxFilterSelect,
  AxSegments,
  AxStatusKeys,
  AxAvatar,
  AxEmptyDetail,
  AxExcelIcon,
  AxPdfIcon,
  AxPlusIcon,
  AxPrintIcon,
  AxRefreshIcon,
  AxSendIcon,
  AxTableIcon,
} from "../../components/adminsitration/ui";
import EmployeeDetail from "../../components/master/employee/EmployeeDetail";
import EmployeeExpiryDrawer from "../../components/master/employee/EmployeeExpiryDrawer";
import {
  RANK,
  blankEmployee,
  daysBetween,
  fmtDate,
  iso,
  mergeLookups,
  missingDocs,
  money,
  payOf,
  probationEnd,
  seedDocs,
  toDraft,
  today,
  worstDoc,
} from "../../components/master/employee/employeeModel";

const SORTS = [
  { key: "name", label: "Name" },
  { key: "doj", label: "Joined" },
  { key: "expiry", label: "Expiry" },
];

const I = {
  warn: (
    <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M7 2 13 12H1z" />
      <path d="M7 5.6v2.6M7 10h.01" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="7" cy="7" r="5.2" />
      <path d="M7 4.2V7l1.8 1.2" />
    </svg>
  ),
  paper: (
    <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3.6 1.6h4.2l2.6 2.6v8.2H3.6z" />
      <path d="M7.8 1.6v2.6h2.6" />
    </svg>
  ),
  cal: (
    <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M2.4 3.4h9.2v9.2H2.4z" />
      <path d="M4.8 1.4v3M9.2 1.4v3M2.4 6.4h9.2" />
    </svg>
  ),
  ok: (
    <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2.6 7.4 5.6 10.4 11.4 4" />
    </svg>
  ),
};

const RAIL = [
  { k: "expired", cls: "crit", lbl: "expired", ic: I.warn, seg: "expiring" },
  { k: "critical", cls: "crit", lbl: "expiring in 15 days", ic: I.clock, seg: "expiring" },
  { k: "warning", cls: "warnx", lbl: "expiring in 30 days", ic: I.clock, seg: "expiring" },
  { k: "watch", cls: "info", lbl: "expiring in 90 days", ic: I.clock, seg: "expiring" },
  { k: "missing", cls: "warnx", lbl: "missing a required paper", ic: I.paper, seg: "missing" },
  { k: "prob", cls: "info", lbl: "probation ending", ic: I.cal, seg: "prob" },
];

const MySwal = withReactContent(Swal);
const csvCell = (x) => '"' + String(x == null ? "" : x).replace(/"/g, '""') + '"';

const Employee = () => {
  const dispatch = useDispatch();
  const { branch, branchlist } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const { togglehead } = useSelector((state) => state.headerToggle);

  const [lk, setLk] = useState(() => mergeLookups(null));
  const [rows, setRows] = useState([]);
  const [apiMissing, setApiMissing] = useState(false);
  const [q, setQ] = useState("");
  const [fDept, setFDept] = useState("");
  const [fDesig, setFDesig] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [fType, setFType] = useState("");
  const [seg, setSeg] = useState("all");
  const [railKey, setRailKey] = useState(null);
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [picked, setPicked] = useState({});
  const [tableView, setTableView] = useState(false);
  const [open, setOpen] = useState(null); // { record, isNew, tab, rev }
  const [drawer, setDrawer] = useState(false);
  const [bulkBranch, setBulkBranch] = useState("");
  const rev = useRef(0);

  // Until the backend adds an "Employee" menu row, nobody would have a
  // permission for this screen; fall back to full access so it can be used.
  // Remove the fallback once the menu row ships (see the gaps doc, section 1).
  const permit = useMemo(() => {
    const row = menuItems?.menuPermissions?.find?.((e) => e.menu?.name === "Employee");
    return row || { view: true, add: true, edit: true, deletion: true };
  }, [menuItems]);

  /* ------------------------------------------------------------ data */
  const load = useCallback(async () => {
    if (!branch?.guid) return;
    try {
      const data = await getEmployeeList(branch.guid);
      const mapped = (Array.isArray(data) ? data : []).map((r) => toDraft(r, lk));
      setRows(mapped);
      setApiMissing(false);
      return mapped;
    } catch (error) {
      console.error("Error loading employees:", error);
      setRows([]);
      setApiMissing(true);
      return [];
    }
  }, [branch, lk]);

  useEffect(() => {
    dispatch(fetchBranchList());
    getEmployeeLookups()
      .then((d) => setLk(mergeLookups(d)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const branchName = useCallback(
    (g) => (branchlist || []).find((b) => b.guid === g)?.name || "—",
    [branchlist]
  );

  /* ------------------------------------------------------------ filters */
  const active = useMemo(() => rows.filter((e) => e.status !== "Left"), [rows]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      active: rows.filter((e) => e.status === "Active").length,
      prob: rows.filter((e) => e.status === "On probation").length,
      expiring: active.filter((e) => RANK[worstDoc(e, lk).k] <= 2).length,
      missing: rows.filter((e) => missingDocs(e, lk).length).length,
      sales: active.filter((e) => e.isSalesman).length,
      left: rows.length - active.length,
    }),
    [rows, active, lk]
  );

  const alerts = useMemo(() => {
    const c = { expired: 0, critical: 0, warning: 0, watch: 0, missing: 0, prob: 0 };
    active.forEach((e) => {
      const seen = {};
      (e.docs || []).forEach((d) => {
        const k = worstDoc({ docs: [d] }, lk).k;
        if (k !== "valid") seen[k] = 1;
      });
      Object.keys(seen).forEach((k) => {
        c[k] += 1;
      });
      if (missingDocs(e, lk).length) c.missing += 1;
      if (e.status === "On probation") {
        const end = probationEnd(e);
        if (end && daysBetween(today(), end) <= 15) c.prob += 1;
      }
    });
    return c;
  }, [active, lk]);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((e) => {
      if (fDept && e.dept !== fDept) return false;
      if (fDesig && e.desig !== fDesig) return false;
      if (fBranch && (e.branches || []).indexOf(fBranch) < 0) return false;
      if (fType && e.empType !== fType) return false;
      if (seg === "active" && e.status !== "Active") return false;
      if (seg === "prob" && e.status !== "On probation") return false;
      if (seg === "sales" && !(e.isSalesman && e.status !== "Left")) return false;
      if (seg === "left" && e.status !== "Left") return false;
      if (seg === "missing" && !missingDocs(e, lk).length) return false;
      if (seg === "expiring") {
        if (e.status === "Left") return false;
        const w = worstDoc(e, lk).k;
        // a rail chip narrows "expiring" to its own band
        if (railKey && ["expired", "critical", "warning", "watch"].indexOf(railKey) > -1) {
          if (!(e.docs || []).some((d) => worstDoc({ docs: [d] }, lk).k === railKey)) return false;
        } else if (RANK[w] > 2) return false;
      }
      if (seg === "prob" && railKey === "prob") {
        const end = probationEnd(e);
        if (!end || daysBetween(today(), end) > 15) return false;
      }
      if (needle) {
        const hay = [e.name, e.code, e.desig, e.dept, e.phone1, e.phone2, e.email, e.smCode, e.bioId]
          .concat((e.docs || []).map((d) => d.no))
          .join(" ")
          .toLowerCase();
        if (hay.indexOf(needle) < 0) return false;
      }
      return true;
    });
    const { key, dir } = sort;
    out = out.slice().sort((a, b) => {
      if (key === "doj") return (a.doj < b.doj ? -1 : a.doj > b.doj ? 1 : 0) * dir;
      if (key === "expiry") {
        const x = worstDoc(a, lk);
        const y = worstDoc(b, lk);
        if (RANK[x.k] !== RANK[y.k]) return (RANK[x.k] - RANK[y.k]) * dir;
        return ((x.days === null ? 1e9 : x.days) - (y.days === null ? 1e9 : y.days)) * dir;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) * dir;
    });
    return out;
  }, [rows, q, fDept, fDesig, fBranch, fType, seg, railKey, sort, lk]);

  const clearFilters = () => {
    setQ("");
    setFDept("");
    setFDesig("");
    setFBranch("");
    setFType("");
    setSeg("all");
    setRailKey(null);
  };

  /* ------------------------------------------------------------ open / new */
  const openEmp = (e, tab) => {
    rev.current += 1;
    setOpen({ record: e, isNew: false, tab: tab || "profile", rev: rev.current });
  };
  const newEmp = async () => {
    if (!permit?.add) return;
    const blank = blankEmployee(lk, branch);
    blank.docs = seedDocs(blank.docs, blank.empType, lk);
    // ask for the next code before the form opens, so a late answer cannot
    // remount the form under someone who is already typing
    try {
      const code = await getNextEmployeeCode();
      const c = typeof code === "string" ? code : code?.code;
      if (c) blank.code = c;
    } catch (e) {
      /* no endpoint yet — the code stays manual */
    }
    rev.current += 1;
    setOpen({ record: blank, isNew: true, tab: "profile", rev: rev.current });
  };

  const onSaved = async (saved, draft) => {
    const fresh = (await load()) || [];
    // reopen what was just saved: the server's copy if Save returned it, else
    // the reloaded row (found by guid, or by code for a brand-new person)
    const code = (draft.code || "").trim().toLowerCase();
    const rec =
      (saved && saved.guid ? toDraft(saved, lk) : null) ||
      fresh.find((e) => (draft.guid ? e.guid === draft.guid : (e.code || "").toLowerCase() === code));
    rev.current += 1;
    if (!rec) {
      // saved, but the list does not show it (another branch) — close rather
      // than leave a "new" form that would save a duplicate
      setOpen(null);
      return;
    }
    setOpen({ record: rec, isNew: false, tab: "profile", rev: rev.current });
  };

  /* ------------------------------------------------------------ delete */
  const doDelete = (targets) =>
    MySwal.fire({
      title: targets.length === 1 ? "Delete " + targets[0].name + "?" : "Delete " + targets.length + " employees?",
      text: "Documents, pay history and targets go with them. You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      let done = 0;
      for (const e of targets) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await deleteEmployee({ id: e.id, guid: e.guid, Branch: branch, updatedUser: `{${userId?.id}}` });
          if (res) done += 1;
        } catch (error) {
          console.error("Error deleting employee:", error);
        }
      }
      if (open && targets.some((t) => t.guid === open.record.guid)) setOpen(null);
      setPicked({});
      load();
      MySwal.fire({
        title: done ? "Deleted!" : "Error!",
        text: done + " of " + targets.length + " deleted.",
        icon: done ? "success" : "error",
        confirmButtonText: "OK",
      });
    });

  /* ------------------------------------------------------------ bulk */
  const pickedRows = rows.filter((e) => picked[e.guid]);
  const bulk = async (action, extra = {}, okMsg) => {
    if (!pickedRows.length) return;
    try {
      await bulkUpdateEmployees({
        Guids: pickedRows.map((e) => e.guid),
        Action: action,
        ...extra,
        Branch: branch,
        updatedUser: `{${userId?.id}}`,
      });
      toast.success(okMsg);
      setPicked({});
      load();
    } catch (error) {
      console.error("Bulk update failed:", error);
      toast.error("Bulk update failed");
    }
  };

  const bulkBar = pickedRows.length ? (
    <div className="axa-bulk">
      <span className="n">{pickedRows.length} selected</span>
      {permit?.edit && (
        <>
          <select
            className="axe-bulksel"
            value={bulkBranch}
            aria-label="Transfer to branch"
            onChange={(e) => {
              const g = e.target.value;
              setBulkBranch("");
              if (g)
                bulk("TransferBranch", { BranchGuid: g }, pickedRows.length + " moved to " + branchName(g));
            }}
          >
            <option value="">Transfer branch…</option>
            {(branchlist || []).map((b) => (
              <option key={b.guid} value={b.guid}>
                {b.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => bulk("MakeSalesman", {}, "Assigned as salesmen")}>
            Make salesman
          </button>
          <button
            type="button"
            onClick={() =>
              bulk("MarkLeft", { DateOfLeaving: iso(today()) }, pickedRows.length + " marked as left — payroll frozen and access revoked")
            }
          >
            Mark as left
          </button>
        </>
      )}
      <button
        type="button"
        onClick={async () => {
          try {
            await sendRenewalReminder({ Guids: pickedRows.map((e) => e.guid) });
            toast.success("Renewal reminder sent to " + pickedRows.length + " employees and the PRO");
            setPicked({});
          } catch (error) {
            toast.error("Could not send the reminder");
          }
        }}
      >
        <AxSendIcon />
        Renewal reminder
      </button>
      {permit?.deletion && (
        <button type="button" className="del" onClick={() => doDelete(pickedRows)}>
          Delete
        </button>
      )}
      <span className="sp" />
      <button type="button" className="plain" onClick={() => setPicked({})}>
        Clear
      </button>
    </div>
  ) : null;

  /* ------------------------------------------------------------ export */
  const exportColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Code", dataIndex: "code" },
    { title: "Designation", dataIndex: "desig" },
    { title: "Department", dataIndex: "dept" },
    { title: "Branch", dataIndex: "branchName" },
    { title: "Type", dataIndex: "empType" },
    { title: "Nationality", dataIndex: "nationality" },
    { title: "Joined", dataIndex: "doj" },
    { title: "Status", dataIndex: "status" },
    { title: "Mobile", dataIndex: "phone1" },
    { title: "Nearest expiry", dataIndex: "expiry" },
  ];
  const exportData = () => {
    const data = list.map((e) => ({ ...e, branchName: branchName(e.branch), expiry: worstDoc(e, lk).lbl }));
    const fieldMapping = {};
    exportColumns.forEach((c) => {
      fieldMapping[c.title] = c.dataIndex;
    });
    return { data, columns: exportColumns, fieldMapping, Rname: "Employee" };
  };
  const exportCsv = () => {
    const head = ["Name", "Code", "Designation", "Department", "Branch", "Type", "Nationality", "Joined", "Status", "Mobile", "Email", "Gross", "Salesman code", "Nearest expiry"];
    const lines = [head.map(csvCell).join(",")];
    list.forEach((e) => {
      lines.push(
        [e.name, e.code, e.desig, e.dept, branchName(e.branch), e.empType, e.nationality, e.doj, e.status, e.phone1, e.email, payOf(e, lk).gross, e.smCode, worstDoc(e, lk).lbl]
          .map(csvCell)
          .join(",")
      );
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    a.download = "employees.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* ------------------------------------------------------------ keyboard */
  const listRef = useRef(list);
  listRef.current = list;
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F3") {
        e.preventDefault();
        const s = document.querySelector(".axa-screen .axa-search");
        if (s) {
          s.focus();
          s.select();
        }
        return;
      }
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        newEmp();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const l = listRef.current;
        if (!l.length) return;
        e.preventDefault();
        const cur = openRef.current?.record?.guid;
        const idx = l.findIndex((x) => x.guid === cur);
        let next = e.key === "ArrowDown" ? idx + 1 : idx - 1;
        next = Math.max(0, Math.min(l.length - 1, next));
        if (permit?.edit || permit?.view) openEmp(l[next]);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permit, lk, branch]);

  /* ------------------------------------------------------------ rows */
  const renderRow = (e) => {
    const w = worstDoc(e, lk);
    const miss = missingDocs(e, lk).length;
    const sd = e.status === "Active" ? "a" : e.status === "On probation" ? "p" : e.status === "Left" ? "l" : "s";
    return {
      name: e.name,
      inactive: e.status === "Left",
      tags: (
        <>
          {e.empType === "Expatriate" ? <span className="axa-tag axe-exp">Expat</span> : <span className="axa-tag axe-loc">Local</span>}
          {e.status === "On probation" ? <span className="axa-tag axe-prob">Probation</span> : null}
          {e.status === "Left" ? <span className="axa-tag axe-left">Left</span> : null}
          {e.isSalesman ? <span className="axa-tag axe-sales">Salesman</span> : null}
        </>
      ),
      sub: (
        <>
          <span className="code">{e.code}</span>
          <span>{e.desig}</span>
          <span>{branchName(e.branch)}</span>
        </>
      ),
      right: (
        <>
          {miss ? (
            <span className="axe-docpill expired">{miss} missing</span>
          ) : w.k !== "valid" ? (
            <span className={"axe-docpill " + w.k}>{w.lbl}</span>
          ) : null}
          <span className={"axe-sdot " + sd} title={e.status} />
        </>
      ),
    };
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      sortKey: "name",
      render: (_, r) => (
        <div className="axa-tcust">
          <AxAvatar name={r.name} />
          <span>{r.name}</span>
        </div>
      ),
    },
    { title: "Code", dataIndex: "code", mono: true },
    { title: "Designation", dataIndex: "desig" },
    { title: "Department", dataIndex: "dept" },
    { title: "Branch", dataIndex: "branch", render: (g) => branchName(g) },
    { title: "Type", dataIndex: "empType" },
    { title: "Nationality", dataIndex: "nationality" },
    { title: "Joined", dataIndex: "doj", sortKey: "doj", mono: true, render: (v) => fmtDate(v) },
    { title: "Status", dataIndex: "status" },
    {
      title: "Nearest expiry",
      dataIndex: "docs",
      sortKey: "expiry",
      render: (_, r) => {
        const w = worstDoc(r, lk);
        return <span className={"axe-st " + w.k}>{w.lbl}</span>;
      },
    },
    { title: "Mobile", dataIndex: "phone1", mono: true },
    { title: "Gross", dataIndex: "comps", mono: true, render: (_, r) => money(payOf(r, lk).gross) },
    { title: "Salesman", dataIndex: "smCode", mono: true, render: (v) => v || "—" },
    { title: "Att. ID", dataIndex: "bioId", mono: true },
  ];

  /* ------------------------------------------------------------ rail */
  const rail = (
    <>
      <span className="lb">Needs action</span>
      {RAIL.filter((d) => alerts[d.k]).map((d) => (
        <button
          key={d.k}
          type="button"
          className={"axe-achip " + d.cls + (railKey === d.k ? " on" : "")}
          onClick={() => {
            setSeg(d.seg);
            setRailKey(d.k);
          }}
        >
          <span className="ic">{d.ic}</span>
          <b>{alerts[d.k]}</b> {d.lbl}
        </button>
      ))}
      {rows.length && !alerts.expired && !alerts.critical && !alerts.warning && !alerts.missing ? (
        <span className="axe-achip good">
          <span className="ic">{I.ok}</span>Every paper is in order
        </span>
      ) : null}
      {apiMissing ? (
        <span className="axe-apinote">The Employee API is not on this server yet — see docs/BACKEND-COMPLETE.md (Part 4.3).</span>
      ) : null}
      <span className="sp" />
      <button type="button" className="axe-achip" onClick={clearFilters}>
        Clear filters
      </button>
    </>
  );

  return (
    <AdminScreen
      title="Employee"
      subtitle="People, papers and pay — locals and expatriates in one record"
      searchValue={q}
      onSearchChange={(e) => setQ(e.target.value)}
      onSearchClear={() => setQ("")}
      searchPlaceholder="Name, code, designation, phone, document no…"
      searchHint="F3"
      tableView={tableView}
      rail={rail}
      tools={
        <>
          <button
            type="button"
            className="axa-iconbtn"
            aria-pressed={tableView}
            title="Switch between the split view and the full table"
            onClick={() => setTableView((v) => !v)}
          >
            <AxTableIcon />
            {tableView ? "Split view" : "Table view"}
          </button>
          <span className="axa-sepbar" />
          <button type="button" className="axa-iconbtn" title="Reload" onClick={() => { load(); toast("List refreshed"); }}>
            <AxRefreshIcon />
          </button>
          <button type="button" className="axa-iconbtn" title="Export to CSV" onClick={exportCsv}>
            CSV
          </button>
          <button type="button" className="axa-iconbtn" title="Export to PDF" onClick={() => exportMasterPDF(exportData, "Employee")}>
            <AxPdfIcon />
            PDF
          </button>
          <button type="button" className="axa-iconbtn" title="Export to Excel" onClick={() => exportMasterExcel(exportData, "Employee")}>
            <AxExcelIcon />
            Excel
          </button>
          <button type="button" className="axa-iconbtn" title="Print" onClick={() => printMasterReport(exportData, "Employee")}>
            <AxPrintIcon />
          </button>
          <button
            type="button"
            className="axa-iconbtn"
            aria-pressed={!!togglehead}
            title="Collapse the app header"
            onClick={() => dispatch(setheadertoggle(!togglehead))}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M4 10l4-4 4 4" />
            </svg>
          </button>
        </>
      }
      actions={
        <>
          <button type="button" className="axa-btn axa-btn-teal" onClick={() => setDrawer(true)}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="8" cy="8" r="6" />
              <path d="M8 4.6V8l2.4 1.6" />
            </svg>
            Document expiry
          </button>
          {permit?.add && (
            <button type="button" className="axa-btn axa-btn-primary" onClick={newEmp}>
              <AxPlusIcon />
              New employee
            </button>
          )}
        </>
      }
      filters={
        <>
          <AxFilterSelect value={fDept} onChange={(e) => setFDept(e.target.value)} placeholder="All departments" options={lk.departments} />
          <AxFilterSelect value={fDesig} onChange={(e) => setFDesig(e.target.value)} placeholder="All designations" options={lk.designations} />
          <AxFilterSelect
            value={fBranch}
            onChange={(e) => setFBranch(e.target.value)}
            placeholder="All branches"
            options={(branchlist || []).map((b) => ({ value: b.guid, label: b.name }))}
          />
          <AxFilterSelect value={fType} onChange={(e) => setFType(e.target.value)} placeholder="Local and expat" options={["Local", "Expatriate"]} />
        </>
      }
      segments={
        <AxSegments
          value={seg}
          onChange={(k) => {
            setSeg(k);
            setRailKey(null);
          }}
          items={[
            { key: "all", label: "All", count: counts.all },
            { key: "active", label: "Active", count: counts.active },
            { key: "prob", label: "Probation", count: counts.prob },
            { key: "expiring", label: "Expiring papers", count: counts.expiring },
            { key: "missing", label: "Missing papers", count: counts.missing },
            { key: "sales", label: "Salesmen", count: counts.sales },
            { key: "left", label: "Left", count: counts.left },
          ]}
        />
      }
      list={
        <AdminList
          items={list}
          keyOf={(e) => e.guid}
          selectedKey={open && !open.isNew ? open.record.guid : null}
          onSelect={permit?.edit || permit?.view ? (e) => openEmp(e) : undefined}
          picked={picked}
          onPick={(key, on) =>
            setPicked((p) => {
              const next = { ...p };
              if (on) next[key] = true;
              else delete next[key];
              return next;
            })
          }
          onPickAll={(on) => {
            if (!on) return setPicked({});
            const next = {};
            list.forEach((e) => {
              next[e.guid] = true;
            });
            return setPicked(next);
          }}
          renderRow={renderRow}
          sorts={SORTS}
          sort={sort}
          onSort={(key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))}
          bulkBar={bulkBar}
          table={tableView}
          columns={tableColumns}
          onRowEdit={permit?.edit ? (e) => { setTableView(false); openEmp(e); } : undefined}
          onRowDelete={permit?.deletion ? (e) => doDelete([e]) : undefined}
          emptyTitle={apiMissing ? "Could not load employees" : rows.length ? "No employee matches" : "No employees yet"}
          emptyHint={
            apiMissing
              ? "The server has no Employee API yet."
              : rows.length
              ? "Try a shorter search, or clear a filter."
              : "Press N or New employee to add the first one."
          }
        />
      }
      detail={
        open ? (
          <EmployeeDetail
            key={open.rev}
            record={open.record}
            isNew={open.isNew}
            initialTab={open.tab}
            lookups={lk}
            branchlist={branchlist}
            branch={branch}
            userId={userId}
            employees={rows}
            canSave={open.isNew ? !!permit?.add : !!permit?.edit}
            canDelete={!!permit?.deletion}
            onSaved={onSaved}
            onClose={() => setOpen(null)}
            onDelete={(d) => doDelete([d])}
          />
        ) : (
          <AxEmptyDetail title="Pick someone from the list">
            Or press N to add a new employee. Everything about a person — papers, pay and targets — lives on this
            one screen.
          </AxEmptyDetail>
        )
      }
      status={
        <AxStatusKeys
          keys={[
            { k: "F3", t: "Search" },
            { k: "N", t: "New" },
            { k: "↑↓", t: "Move" },
            { k: "F9", t: "Save" },
          ]}
          right={list.length + " of " + rows.length + " employees in " + (branch?.name || "this branch")}
        />
      }
    >
      <EmployeeExpiryDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        employees={rows}
        lookups={lk}
        branchName={branchName}
        onOpenEmployee={(e) => {
          setDrawer(false);
          setTableView(false);
          openEmp(e, "docs");
        }}
      />
    </AdminScreen>
  );
};

export default Employee;
